import { Component, computed, inject, input, viewChild } from '@angular/core';
import { DocumentEmailService } from '../services/document-email.service';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { iif, lastValueFrom, of } from 'rxjs';
import { UserIdToNamePipe } from '../pipes/user-id-to-name.pipe';
import { AsyncPipe, DOCUMENT } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { SignedUrlService } from '../services/signed-url.service';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { StatusIconComponent } from './status-icon/status-icon.component';
import { ConfigService } from '../services/config.service';
import { MomentModule } from 'ngx-moment';
import { MatTooltip } from '@angular/material/tooltip';
import { DocketLinkComponent } from './docket-link/docket-link.component';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Component({
  selector: 'app-email-list',
  standalone: true,
  imports: [
    UserIdToNamePipe,
    AsyncPipe,
    MatTableModule,
    MatIcon,
    MatButtonModule,
    StatusIconComponent,
    MomentModule,
    MatTooltip,
    DocketLinkComponent,
    MatPaginatorModule
  ],
  templateUrl: './email-list.component.html',
  providers: [DocumentEmailService, SignedUrlService]
})
export class EmailListComponent {
  private emailService = inject(DocumentEmailService);
  private signedUrlService = inject(SignedUrlService);
  private document = inject(DOCUMENT);
  dateFormat = ConfigService.dates.shortDateTimeFormat;

  source = input.required<{ resource: string; id: number }>();
  linkToDocket = input<boolean>(false);

  breakpointObserver = inject(BreakpointObserver);

  columns = computed(() => {
    if (this.breakpointObserver.isMatched('(max-width: 599px)')) {
      return ['combined'];
    }
    return [
      this.linkToDocket() ? 'linkToDocket' : null,
      'subject',
      'to',
      'from',
      'status',
      'created_at',
      'link'
    ].filter(Boolean);
  });

  emailsQuery = injectQuery(() => ({
    enabled: Boolean(this.source),
    queryKey: ['emails', this.source()?.resource, this.source()?.id],
    queryFn: () =>
      this.source()?.id
        ? lastValueFrom(
            this.emailService.getEmails(
              this.source()?.resource,
              this.source()?.id
            )
          )
        : Promise.resolve([])
  }));

  paginator = viewChild(MatPaginator);

  datasource = computed(() => {
    const datasource = new MatTableDataSource(this.emailsQuery.data());
    datasource.paginator = this.paginator();
    return datasource;
  });

  downloadEmail(url: string) {
    this.signedUrlService.getSignedUrl(url).subscribe(({ url }) => {
      // emulate clicking on this link
      const anchor = this.document.createElement('a');
      anchor.href = url;
      anchor.click();
    });
  }
}
